'use client';

import { useState } from 'react';

interface CalculatorCelsiusFahrenheitProps {}

export default function CalculatorCelsiusFahrenheit({}: CalculatorCelsiusFahrenheitProps) {
  const [celsius, setCelsius] = useState<string>('0');
  const [fahrenheit, setFahrenheit] = useState<string>('32');

  const handleCelsiusChange = (value: string) => {
    setCelsius(value);
    const celsiusNum = parseFloat(value);
    if (!isNaN(celsiusNum)) {
      setFahrenheit(((celsiusNum * 9/5) + 32).toFixed(1));
    } else {
      setFahrenheit('');
    }
  };

  const handleFahrenheitChange = (value: string) => {
    setFahrenheit(value);
    const fahrenheitNum = parseFloat(value);
    if (!isNaN(fahrenheitNum)) {
      setCelsius(((fahrenheitNum - 32) * 5/9).toFixed(1));
    } else {
      setCelsius('');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-rose-50">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-slate-900 mb-4">
            Celsius ↔ Fahrenheit Converter
          </h1>
          <p className="text-lg text-slate-600">
            Convert temperatures between Celsius and Fahrenheit instantly
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
          <div className="space-y-8">
            {/* Celsius Input */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-3">
                Celsius
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={celsius}
                  onChange={(e) => handleCelsiusChange(e.target.value)}
                  className="w-full px-6 py-4 text-2xl font-bold text-slate-900 bg-slate-50 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-orange-500/20 focus:border-orange-500 transition-all"
                  placeholder="Enter Celsius"
                  step="0.1"
                />
                <span className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-400 font-semibold">
                  °C
                </span>
              </div>
            </div>

            {/* Conversion Arrow */}
            <div className="flex justify-center">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center text-white text-2xl">
                ↕
              </div>
            </div>

            {/* Fahrenheit Input */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-3">
                Fahrenheit
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={fahrenheit}
                  onChange={(e) => handleFahrenheitChange(e.target.value)}
                  className="w-full px-6 py-4 text-2xl font-bold text-slate-900 bg-slate-50 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-red-500/20 focus:border-red-500 transition-all"
                  placeholder="Enter Fahrenheit"
                  step="0.1"
                />
                <span className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-400 font-semibold">
                  °F
                </span>
              </div>
            </div>
          </div>

          {/* Quick Reference */}
          <div className="mt-12 pt-8 border-t border-slate-200">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Common Temperatures</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <div className="text-sm text-slate-500 mb-1">Freezing</div>
                <div className="text-xl font-bold text-orange-600">0°C</div>
                <div className="text-sm text-slate-600 mt-1">32°F</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <div className="text-sm text-slate-500 mb-1">Room Temp</div>
                <div className="text-xl font-bold text-orange-600">20°C</div>
                <div className="text-sm text-slate-600 mt-1">68°F</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <div className="text-sm text-slate-500 mb-1">Body Temp</div>
                <div className="text-xl font-bold text-orange-600">37°C</div>
                <div className="text-sm text-slate-600 mt-1">98.6°F</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <div className="text-sm text-slate-500 mb-1">Boiling</div>
                <div className="text-xl font-bold text-orange-600">100°C</div>
                <div className="text-sm text-slate-600 mt-1">212°F</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}